import {
  BaseModal,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ArrowDownIcon, CalendarIcon, TimeIcon, UserIcon } from 'cokitchen-icons';
import { Calendar } from 'react-date-range';
import TimeSelector from 'components/base/time-selector';
import { TimeSelectorType } from 'components/base/time-selector/types';
import { CoKitchenType } from 'models/cokitchen.model';
import moment from 'moment';
import React, { useState } from 'react';
import { STORAGE_KEYS } from 'utils/constants';
import { ExternalRiderType } from 'models/external-rider.model';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import { ENUM_AUTHORIZE_TYPE } from 'utils/enum';

type FooterProps = {
  loading: boolean;
  onConfirm: () => void;
  closeModal: () => void;
  disabled: boolean;
};

type Props = {
  closeModal: () => void;
  onSuccess: () => void;
  riders: ExternalRiderType[];
};

const ScheduledRiderFooter = ({ onConfirm, loading, closeModal, disabled }: FooterProps) => (
  <div className='flex items-center justify-between gap-3 '>
    <Button
      small
      color='primary'
      variant='outline'
      className='mx-auto'
      disabled={loading}
      onClick={closeModal}>
      Close
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      disabled={disabled}
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const ScheduledRiderModal = ({ closeModal, onSuccess, riders }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const allCokitchens: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
  );

  const [formDetails, setFormDetails] = useState({
    date: '',
    timeFrom: {} as TimeSelectorType,
    timeTo: {} as TimeSelectorType,
    cokitchen: {} as CoKitchenType
  });

  const handleChange = (key: keyof typeof formDetails, value: any) => {
    setFormDetails((prev) => ({ ...prev, [key]: value }));
  };

  const handleScheduleRider = async () => {
    try {
      setLoading(true);

      await apiService.post('rider-applications/bulk', {
        data: {
          date: formDetails.date,
          start_time: `${formDetails.timeFrom.hours}:${formDetails.timeFrom.minutes}${formDetails.timeFrom.amPm}`,
          end_time: `${formDetails.timeTo.hours}:${formDetails.timeTo.minutes}${formDetails.timeTo.amPm}`,
          cokitchen_id: formDetails.cokitchen.cokitchen_id,
          application_ids: riders.map((rider) => rider.application_id),
          action: ENUM_AUTHORIZE_TYPE.SCHEDULE
        },
        baseUrl: process.env.REACT_APP_AUTH_API_URL
      });
      onSuccess();
      toast(<ToastMessage type='success' message='Rider successfully scheduled' />);
      closeModal();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={'An error ocecured'} />);
    } finally {
      setLoading(false);
    }
  };

  const disableBtn =
    !formDetails.date ||
    !formDetails.timeFrom.hours ||
    !formDetails.timeTo.hours ||
    !formDetails.cokitchen.cokitchen_id;

  return (
    <BaseModal
      isOpen={true}
      title='In-person Approval'
      description='Input the details for riders in-person approval'
      titleIcon={<UserIcon className='text-blue-5' />}
      titleIconColor='info'
      onRequestClose={closeModal}
      footer={
        <ScheduledRiderFooter
          loading={loading}
          disabled={disableBtn}
          onConfirm={handleScheduleRider}
          closeModal={closeModal}
        />
      }>
      <div className='space-y-5 mt-3'>
        <div>
          <p className='font-medium text-base mb-2'>Select time</p>
          <Dropdown
            content={
              <div className='flex justify-between items-center cursor-pointer border border-gray-1 rounded-md p-3 text-gray-6/40 font-medium'>
                <span className={`${formDetails.timeFrom ? 'text-gray-6' : 'text-gray-6/40'}`}>
                  {`${formDetails.timeFrom.hours}:${formDetails.timeFrom.minutes} ${formDetails.timeFrom.amPm} `}
                  -
                  {` ${formDetails.timeTo.hours}:${formDetails.timeTo.minutes} ${formDetails.timeTo.amPm}`}
                </span>
                <TimeIcon className='text-gray-6/40' />
              </div>
            }>
            <DropdownList className='p-5 space-y-4'>
              <div>
                <span className='text-gray-6/70 text-sm mb-2 inline-block'>From</span>
                <TimeSelector
                  value={formDetails?.timeFrom}
                  onChange={(v) => handleChange('timeFrom', v)}
                />
              </div>
              <div>
                <span className='text-gray-6/70 text-sm mb-2 inline-block'>To</span>
                <TimeSelector
                  value={formDetails.timeTo}
                  onChange={(v) => handleChange('timeTo', v)}
                />
              </div>
            </DropdownList>
          </Dropdown>
        </div>
        <div>
          <p className='font-medium text-base mb-2'>Select date</p>
          <Dropdown
            content={
              <div className='flex justify-between items-center cursor-pointer border border-gray-1 rounded-md p-3 text-gray-6/40 font-medium'>
                <span className={`${formDetails.date ? 'text-gray-6' : 'text-gray-6/40'}`}>
                  {formDetails.date
                    ? moment(new Date(formDetails.date)).format('ddd MMM DD, yyyy')
                    : 'Select date'}
                </span>
                <CalendarIcon className='text-gray-6/40' />
              </div>
            }>
            <DropdownList className='min-h-80'>
              <Calendar
                onChange={(date) =>
                  handleChange('date', moment(date).format('YYYY-MM-DDTHH:mm:ssZ'))
                }
                date={formDetails.date ? new Date(formDetails.date) : undefined}
              />
            </DropdownList>
          </Dropdown>
        </div>
        <div>
          <p className='font-medium text-base mb-2'>Select time</p>
          <Dropdown
            content={
              <div className='flex justify-between items-center cursor-pointer border border-gray-1 rounded-md p-3 text-gray-6/40 font-medium'>
                <span
                  className={`${formDetails.cokitchen.name ? 'text-gray-6' : 'text-gray-6/40'}`}>
                  {formDetails.cokitchen.name ?? 'Select Cokitchen'}
                </span>
                <ArrowDownIcon className='text-gray-6/40' />
              </div>
            }>
            <DropdownList className='max-h-[300px] min-w-[200px]'>
              {allCokitchens.map((p, i) => (
                <DropdownItem key={i} onClick={() => handleChange('cokitchen', p)}>
                  <Input
                    type='radio'
                    checked={formDetails.cokitchen.cokitchen_id === p.cokitchen_id}
                    readOnly
                    label={p.name}
                  />
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </div>
      </div>
    </BaseModal>
  );
};

export default ScheduledRiderModal;
